import type { parseFilterModel } from '@/lib/createAgGridDatasource';
import type { SortColumnState } from '@/lib/downloadOptions';
import { toast } from '@nautilusbio/nb-design-system/Toaster';
import type { UseMutationResult } from '@tanstack/react-query';
import { useCallback, type RefObject } from 'react';

/** The subset of AG Grid's `GridApi` that `useCsvDownloadHandler` reads. */
export interface DownloadGridApi {
  getColumnState(): readonly SortColumnState[];
  getFilterModel(): Parameters<typeof parseFilterModel>[0];
}

/** For a ref whose `.current` already is the `GridApi` (e.g. an `onGridReady`-populated ref). */
export const apiFromGridApiRef = <T extends DownloadGridApi>(ref: T | null): T | null | undefined => ref;

/** For a ref whose `.current` is an `AgGridReact` wrapper — reads its `.api`. */
export const apiFromAgGridReactRef = <T extends DownloadGridApi>(ref: { api: T | null } | null): T | null | undefined =>
  ref?.api;

/** A Download button's onPress: exports with the grid's live sort/filter state, with one shared error path. */
export function useCsvDownloadHandler<TGridRef, TOptions>(
  gridRef: RefObject<TGridRef>,
  extractApi: (ref: TGridRef | null) => DownloadGridApi | null | undefined,
  buildOptions: (
    columnState: readonly SortColumnState[],
    filterModel: Parameters<typeof parseFilterModel>[0],
  ) => TOptions,
  download: Pick<UseMutationResult<string, Error, TOptions>, 'mutate'>,
  errorMessage: string,
): () => void {
  return useCallback(() => {
    const reportFailure = (error: unknown) => {
      console.error('[useCsvDownloadHandler] download failed', error);
      toast.error(errorMessage);
    };
    let options: TOptions;
    try {
      const api = extractApi(gridRef.current);
      options = buildOptions(api?.getColumnState() ?? [], api?.getFilterModel() ?? {});
    } catch (error) {
      // A malformed filter model throws here, before mutate, so onError would never fire.
      reportFailure(error);
      return;
    }
    download.mutate(options, { onError: reportFailure });
  }, [gridRef, extractApi, buildOptions, download, errorMessage]);
}
